import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, HStack, IconButton, Table, Text, VStack } from "@chakra-ui/react";
import { HiArrowRight, HiOutlineUserMinus } from "react-icons/hi2";
import ScenarioService from "@/services/agency/ScenarioService";
import { showToast } from "@/components/ui/toaster";
import ConfirmationButton from "@/components/ui/foundation/ConfirmationButton";
import AssignScenarioToClientModal from "@/pages/clients/components/AssignScenarioToClientModal";
import { DetailTableShell, ProductLineBadge, tableHeaderStyle } from "@/components/ui/detail/primitives";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("pl-PL") : "—");

const COLUMNS = ["Nazwa scenariusza", "Typ", "Model", "Utworzono", "Akcje"];

const ClientScenariosTab = ({ clientId, scenarios, onChanged }) => {
    const navigate = useNavigate();
    const [isAssignOpen, setIsAssignOpen] = useState(false);
    const [unassigningId, setUnassigningId] = useState(null);

    const handleUnassign = (scenario) => {
        setUnassigningId(scenario.id);
        ScenarioService.unassignFromOwner(scenario.id)
            .then(() => {
                showToast("Odpięto", `Scenariusz „${scenario.name}” odpięty od klienta`, "success");
                onChanged();
            })
            .catch((err) => showToast("Błąd", err?.response?.data ?? "Nie udało się odpiąć scenariusza", "error"))
            .finally(() => setUnassigningId(null));
    };

    return (
        <VStack w="full" align="flex-start" gap={3} pt={4}>
            <HStack w="full" justify="flex-end">
                <Button variant="primary" size="sm" onClick={() => setIsAssignOpen(true)}>
                    Przypisz scenariusz
                </Button>
            </HStack>

            <DetailTableShell>
                <Table.Root>
                    <Table.Header>
                        <Table.Row bg="bg.card" borderBottom="1px solid" borderColor="border.primary">
                            {COLUMNS.map((col, i) => (
                                <Table.ColumnHeader key={col} {...tableHeaderStyle} textAlign={i === COLUMNS.length - 1 ? "right" : "left"}>
                                    {col}
                                </Table.ColumnHeader>
                            ))}
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {scenarios.length === 0 ? (
                            <Table.Row bg="bg.secondary">
                                <Table.Cell colSpan={COLUMNS.length} py={8} border="none" textAlign="center">
                                    <Text color="text.grey" fontSize="sm">Brak przypisanych scenariuszy</Text>
                                </Table.Cell>
                            </Table.Row>
                        ) : scenarios.map((s) => (
                            <Table.Row key={s.id} bg="bg.secondary" borderBottom="1px solid" borderColor="border.primary" _hover={{ bg: "bg.highlight" }} transition="background 0.15s">
                                <Table.Cell py={4} px={5} border="none">
                                    <Text color="white" fontSize="sm" fontWeight={500}>{s.name}</Text>
                                </Table.Cell>
                                <Table.Cell py={4} px={5} border="none">
                                    <ProductLineBadge type={s.type} />
                                </Table.Cell>
                                <Table.Cell py={4} px={5} border="none">
                                    {s.agent?.id ? (
                                        <Text
                                            as="button"
                                            type="button"
                                            color="brand.300"
                                            fontSize="sm"
                                            _hover={{ textDecoration: "underline" }}
                                            onClick={() => navigate(`/models/${s.agent.id}`)}
                                        >
                                            {s.agent.name}
                                        </Text>
                                    ) : (
                                        <Text color="text.grey" fontSize="sm">—</Text>
                                    )}
                                </Table.Cell>
                                <Table.Cell py={4} px={5} border="none">
                                    <Text color="text.grey" fontSize="sm">{fmtDate(s.createdAtUtc)}</Text>
                                </Table.Cell>
                                <Table.Cell py={4} px={5} border="none" textAlign="right">
                                    <HStack gap={2} justify="flex-end">
                                        <ConfirmationButton
                                            variant="outlined"
                                            size="sm"
                                            minW="auto"
                                            label=""
                                            aria-label="Odepnij scenariusz od klienta"
                                            icon={<HiOutlineUserMinus />}
                                            onConfirm={() => handleUnassign(s)}
                                            isLoading={unassigningId === s.id}
                                        />
                                        <IconButton variant="outlined" size="sm" aria-label="Szczegóły scenariusza" onClick={() => navigate(`/scenarios/${s.id}`)}>
                                            <HiArrowRight />
                                        </IconButton>
                                    </HStack>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table.Root>
            </DetailTableShell>

            <AssignScenarioToClientModal
                isOpen={isAssignOpen}
                onClose={() => setIsAssignOpen(false)}
                clientId={clientId}
                onAssigned={onChanged}
            />
        </VStack>
    );
};

export default ClientScenariosTab;
